import { csrf } from './csrf';
import { keys as headerKeys, values as headerValues } from './headers';
import { log } from './log';
import { CoveoEmail, CoveoEnableSearch, CoveoToken } from './keys';
import { request } from './request';
import { timers } from './timers';
import { coveoToken as coveoTokenURL } from './urls';
import { o } from './observable';
import { appShadowRoot } from './elements';

const searchBox = appShadowRoot.getElementById('CoveoSearchBox');
const enableButton = appShadowRoot.getElementById('EnableSearch');

async function retrieve(email = '', token = '') {
  let result = null;
  let err = null;
  let status = 0;

  try {
    const res = await request(coveoTokenURL, {
      credentials: 'include',
      method: 'POST',
      headers: {
        [headerKeys.accept]: headerValues.json,
        [headerKeys.ctype]: headerValues.json,
        [headerKeys.csrf]: token,
      },
      body: JSON.stringify({ email }),
    });

    status = res.status;

    if (res.ok) {
      const data = await res.json();

      if ((data.data || '').length > 0) {
        result = data.data;
      } else {
        err = new Error('Could not retrieve token');
      }
    } else {
      err = new Error('Could not retrieve token');
    }
  } catch (lerr) {
    err = lerr;
  }

  return [result, err, status];
}

export async function coveoToken(data = {}, max = 5) {
  const email =
    data.email ||
    sessionStorage.getItem(CoveoEmail) ||
    `exl-anonymous-${Math.floor(Math.random() * 1e6)}@experienceleague.local`;
  const tries = Array(10).map((i, idx) => idx + 1);
  let ctoken = '';
  let lcsrfToken = '';
  let i = 0;
  let n = 50;
  const delay = 1000;

  while (lcsrfToken.length === 0 && ++i < max) {
    try {
      lcsrfToken = await csrf(coveoTokenURL);

      // Check if still empty after the call
      if (lcsrfToken.length === 0) {
        log(`Failed to retrieve CSRF token for Coveo, retrying [${i}]`);
        // Wait for a bit before trying again
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    } catch (err) {
      log("Couldn't retrieve CSRF token for Coveo, trying again");
      // Wait for a bit before trying again
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // Max attempts reached, handle the failure appropriately here
  if (lcsrfToken.length === 0) {
    log(`Failed to retrieve CSRF token after ${max} attempts.`);
  }

  if (lcsrfToken.length > 0) {
    sessionStorage.setItem(CoveoEmail, email);

    if (window.location.hostname === 'experienceleague.adobe.com') {
      for (const t of tries) {
        const [ldata, lerror] = await retrieve(email, lcsrfToken);

        if (ldata !== null && lerror === null) {
          ctoken = ldata;
          break;
        } else {
          log(lerror?.message ?? `Failed to retrieve Coveo token [${t}]`);
        }
      }
    } else {
      ctoken = 'xxcfe1b6e9-3628-49b5-948d-ed50d3fa6c99';
    }
  } else {
    log('Failed to retrieve CSRF token for Coveo access token request');
  }

  if (ctoken.length > 0) {
    sessionStorage.setItem(CoveoToken, ctoken);
    n = 6e4 * 5;
    log(`Retrieved Coveo token, set in sessionStorage as "${CoveoToken}"; expiring in ${n}ms`);
  } else {
    sessionStorage.removeItem(CoveoToken);
    log(`Failed to retrieve Coveo token; retrying in ${n}ms`);
  }

  if (timers.has(CoveoToken)) {
    clearTimeout(timers.get(CoveoToken));
  }

  timers.set(
    CoveoToken,
    () => {
      timers.delete(CoveoToken);
      coveoToken(data);
    },
    n,
  );

  if (ctoken !== null && ctoken !== '') {
    o.dispatch('coveoToken', ctoken);
  }

  return ctoken;
}

// TESTING Deprecate Notice used under search.html template
// TESTING Deprecate start - - - - - - -
export function EnableSearch() {
  searchBox.style.display = 'inline-flex';
  enableButton.style.display = 'none';

  timers.set(
    CoveoEnableSearch,
    setTimeout(() => {
      timers.delete(CoveoEnableSearch);

      const searchInput = appShadowRoot.querySelector('.magic-box-input input');

      if (searchInput) {
        searchInput.focus();
      }
    }, 500),
  );
}

export function ExitSearch() {
  searchBox.style.display = 'none';
  enableButton.style.display = 'block';
}

// Decorating globals for lib interop
window.EnableSearch = EnableSearch;
window.ExitSearch = ExitSearch;
